import {
  Button,
  Checkbox,
  Flex,
  Grid,
  IconAddLine,
  Modal,
  ScreenReaderContent,
  Text,
  TextInput,
} from "@instructure/ui";
import { t } from "i18next";
import React, { useEffect, useState } from "react";
import { ActionService } from "../../services/ActionService";
import { ExternalService } from "../../services/ExternalService";
import { ReflectionService } from "../../services/ReflectionService";
import { CommonModal } from "../CommonModal";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import { Reflection } from "../Reflection/Reflection";
import { Selector } from "../Selectors/Selector";
import ActionTypesModal from "./ActionTypesModal";
import Skeleton from "react-loading-skeleton";

export const ActionEditPanel = ({
  title,
  contextId,
  action,
  openModal,
  setOpenModal,
  handleActionUpdated,
  handleActionCreated,
  attachedReflections,
  external,
  handleClose,
}) => {
  const showNotification = useNotification();
  const [actionTypes, setActionTypes] = useState([]);
  const [openActionTypeModal, setOpenActionTypeModal] = useState(false);
  const [reflectionList, setReflectionList] = useState([]);
  const [loadReflections, setLoadReflections] = useState(false);
  const [editedAction, setEditedAction] = useState(null);
  const [inProgress, setInProgress] = useState(false);

  useEffect(() => {
    setEditedAction(action);
  }, [action]);

  useEffect(() => {
    const getActionTypes = external
      ? ExternalService.getExternalActionTypes
      : ActionService.getActionTypes;

    getActionTypes()
      .then((actionTypes) => {
        updateActionTypes(actionTypes);
      })
      .catch(() => {
        showNotification(
          t("notifications.general.fetchError", {
            obj: t("actions.actionTypes"),
          }),
          ERROR
        );
      });
  }, [showNotification]);

  useEffect(() => {
    if (!attachedReflections) {
      setLoadReflections(true);
      const getReflections = external
        ? ExternalService.getExternalReflectionsForContext
        : ReflectionService.getReflectionsForContext;

      getReflections(contextId)
        .then((reflections) => {
          setReflectionList(reflections);
        })
        .catch(() => {
          showNotification(
            t("notifications.general.fetchError", {
              obj: t("reflections.reflections"),
            }),
            ERROR
          );
        })
        .finally(() => setLoadReflections(false));
    }
  }, [showNotification]);

  useEffect(() => {
    if (editedAction?.id && editedAction?.reflections === null) {
      setLoadReflections(true);
      const getReflections = external
        ? ExternalService.getExternalReflectionsForContext
        : ReflectionService.getReflectionsForContext;

      getReflections(editedAction.id)
        .then((reflections) => {
          setEditedAction((prevAction) => ({ ...prevAction, reflections }));
        })
        .catch(() => {
          showNotification(
            t("notifications.general.fetchError", {
              obj: t("reflections.reflections"),
            }),
            ERROR
          );
        })
        .finally(() => setLoadReflections(false));
    }
  }, [editedAction, showNotification]);

  const updateActionTypes = (actionTypeList) => {
    setActionTypes(
      actionTypeList.map((actionType) => {
        return { id: actionType.id, label: actionType.name };
      })
    );
  };

  const body = () => {
    return (
      <Modal.Body>
        <Flex wrap="wrap">
          <Flex.Item width="50%" margin="0 small small 0">
            <Selector
              label={t("actions.actionType")}
              id="action-type-select"
              options={actionTypes}
              selectHandler={(actionId) => {
                setEditedAction((prevAction) => ({
                  ...prevAction,
                  action_type: actionTypes.find(
                    (action) => action.id === actionId
                  ),
                }));
              }}
              selectedItemId={editedAction?.action_type?.id || null}
            />
          </Flex.Item>
          <Flex.Item align="end" margin="0 0 small 0">
            <Button
              renderIcon={IconAddLine}
              onClick={() => setOpenActionTypeModal(true)}
            >
              {t("actions.addActionType")}
            </Button>
          </Flex.Item>
          <Flex.Item width="100%" margin="0 0 small 0">
            <TextInput
              width="100%"
              renderLabel={t("actions.actionDesc")}
              value={editedAction?.text || ""}
              onChange={(e) =>
                setEditedAction((prevAction) => ({
                  ...prevAction,
                  text: e.target.value,
                }))
              }
            />
          </Flex.Item>
          {reflectionList.length > 0 && (
            <Flex.Item width="100%" margin="small 0 small 0">
              <Text weight="bold">
                {t("reflections.associatedReflections")}
              </Text>
            </Flex.Item>
          )}
          <Flex.Item shouldGrow margin="0 0 0 small">
            {loadReflections && (
              <Skeleton
                count={3}
                style={{ height: 34, borderRadius: 5, marginBottom: 10 }}
              />
            )}
            <Grid direction="row" vAlign="middle" rowSpacing="none">
              {!attachedReflections &&
                !loadReflections &&
                editedAction?.reflections &&
                reflectionList.map((reflection) => {
                  const selected = !!editedAction.reflections.find(
                    (r) => r.id === reflection.id
                  );
                  return (
                    <Grid.Row key={reflection.id} hAlign="start">
                      <Grid.Col width="auto">
                        <Checkbox
                          label={
                            <ScreenReaderContent>
                              {t("reflections.reflection")}
                            </ScreenReaderContent>
                          }
                          checked={selected}
                          onChange={() => {
                            const newAction = { ...editedAction };
                            if (selected) {
                              newAction.reflections =
                                newAction.reflections.filter(
                                  (r) => r.id !== reflection.id
                                );
                            } else {
                              newAction.reflections.push(reflection);
                            }
                            setEditedAction(newAction);
                          }}
                        />
                      </Grid.Col>
                      <Grid.Col>
                        <Reflection
                          text={reflection.text}
                          author={reflection.author}
                          timestamp={reflection.timestamp}
                        />
                      </Grid.Col>
                    </Grid.Row>
                  );
                })}
              {!!attachedReflections &&
                attachedReflections.map((reflection) => (
                  <Grid.Row key={reflection.id} hAlign={"start"}>
                    <Grid.Col>
                      <Reflection
                        text={reflection.text}
                        author={reflection.author}
                        timestamp={reflection.timestamp}
                      />
                    </Grid.Col>
                  </Grid.Row>
                ))}
            </Grid>
          </Flex.Item>
        </Flex>
      </Modal.Body>
    );
  };

  const handleEditAction = () => {
    const updateAction = external
      ? ExternalService.updateExternalAction
      : ActionService.updateAction;

    updateAction(editedAction)
      .then((updatedAction) => {
        if (handleActionUpdated) {
          handleActionUpdated(updatedAction);
        }
        setOpenActionTypeModal(false);
      })
      .catch(() => {
        showNotification(
          t("notifications.general.updateError", {
            obj: t("notifications.action.obj"),
          }),
          ERROR
        );
      })
      .finally(() => setInProgress(false));
  };

  const addAction = () => {
    const createAction = external
      ? ExternalService.createExternalActionForContext
      : ActionService.createActionForContext;

    createAction(editedAction, contextId)
      .then((newAction) => {
        if (handleActionCreated) {
          handleActionCreated(newAction);
        }
        setOpenActionTypeModal(false);
      })
      .catch(() => {
        showNotification(
          t("notifications.general.createError", {
            obj: t("notifications.action.obj"),
          }),
          ERROR
        );
      })
      .finally(() => setInProgress(false));
  };

  const onSubmitListener = () => {
    if (editedAction.action_type.id && editedAction.text) {
      setInProgress(true);
      if (editedAction.id) {
        handleEditAction();
      } else {
        addAction();
      }
    } else {
      showNotification(t("actions.addActionError"), ERROR);
    }
  };

  return (
    <>
      <CommonModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        label={title}
        size="medium"
        handleSubmit={onSubmitListener}
        body={body()}
        inProgress={inProgress}
        handleClose={handleClose}
      />
      <ActionTypesModal
        addTypesModalOpen={openActionTypeModal}
        setAddTypesModalOpen={setOpenActionTypeModal}
        handleCloseTypesModal={() => setOpenActionTypeModal(false)}
        setTypes={updateActionTypes}
        external={external}
      />
    </>
  );
};
